import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import boardService from "../../services/boardService";

// Async thunks
export const fetchBoards = createAsyncThunk(
  "boards/fetchBoards",
  async (params, { rejectWithValue }) => {
    try {
      const response = await boardService.getAllBoards(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch boards"
      );
    }
  }
);

export const createBoard = createAsyncThunk(
  "boards/createBoard",
  async ({ boardData, imageFile }, { rejectWithValue }) => {
    try {
      const response = await boardService.createBoard(boardData, imageFile);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create board"
      );
    }
  }
);

export const updateBoard = createAsyncThunk(
  "boards/updateBoard",
  async ({ id, boardData, imageFile }, { rejectWithValue }) => {
    try {
      const response = await boardService.updateBoard(id, boardData, imageFile);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update board"
      );
    }
  }
);

export const deleteBoard = createAsyncThunk(
  "boards/deleteBoard",
  async (id, { rejectWithValue }) => {
    try {
      const response = await boardService.deleteBoard(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete board"
      );
    }
  }
);

const boardSlice = createSlice({
  name: "boards",
  initialState: {
    boards: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
    success: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch boards
      .addCase(fetchBoards.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBoards.fulfilled, (state, action) => {
        state.isLoading = false;
        const responseData = action.payload.data || action.payload;
        state.boards = responseData.boards || responseData || [];
      })
      .addCase(fetchBoards.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create board
      .addCase(createBoard.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createBoard.fulfilled, (state, action) => {
        state.isCreating = false;
        const responseData = action.payload.data || action.payload;
        const newBoard = responseData.board || responseData;
        if (newBoard) {
          state.boards.unshift(newBoard);
        }
        state.success = action.payload.message || "Board created successfully";
      })
      .addCase(createBoard.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })
      // Update board
      .addCase(updateBoard.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateBoard.fulfilled, (state, action) => {
        state.isUpdating = false;
        const responseData = action.payload.data || action.payload;
        const updatedBoard = responseData.board || responseData;
        const index = state.boards.findIndex((b) => b.id === updatedBoard.id);
        if (index !== -1) {
          state.boards[index] = updatedBoard;
        }
        state.success = action.payload.message || "Board updated successfully";
      })
      .addCase(updateBoard.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })
      // Delete board
      .addCase(deleteBoard.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteBoard.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.boards = state.boards.filter((b) => b.id !== action.payload.id);
        state.success = action.payload.response.message || "Board deleted successfully";
      })
      .addCase(deleteBoard.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess } = boardSlice.actions;
export default boardSlice.reducer;
